import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/ui/lib/utils';
import { hasSignedInHint } from '../../screens/account/api';
import { ITEM_SAVED_LIST_IDS, itemVariables, v2Session } from './lists';
import { BookmarkIcon } from './icons';

/**
 * "Save" on the restaurant and meal pages (prototype SaveToListModal trigger).
 * Saving needs an account: signed out, it opens the sign-in dialog first, then
 * the save dialog for the same item. Only the button renders on the server;
 * signed in, it asks once whether the item is on any list to show "Saved".
 * Both dialogs (and Radix) are a separate chunk fetched on first intent.
 * Imported only behind ENABLE_LISTS and ENABLE_ACCOUNTS.
 */

type Dialogs = typeof import('./dialogs');

let dialogsPromise: Promise<Dialogs> | null = null;
const loadDialogs = () => {
    dialogsPromise ??= import(
        /* webpackChunkName: "SaveToListDialog" */ './dialogs'
    ).catch(err => {
        dialogsPromise = null;
        throw err;
    });
    return dialogsPromise;
};

type Open = 'none' | 'auth' | 'save';

export interface SaveButtonProps {
    legacyRestaurantId?: string | null;
    legacyMealId?: string | null;
    itemName: string;
    className?: string;
    /**
     * `icon`: a round bookmark for result cards (prototype MealCard / RestaurantCard),
     * the message announced only to screen readers.
     */
    variant?: 'button' | 'icon';
    /** Whether it's saved, when the page already asked for many at once (no request here). */
    initiallySaved?: boolean;
}

export const SaveButton = ({
    legacyRestaurantId,
    legacyMealId,
    itemName,
    className,
    variant = 'button',
    initiallySaved,
}: SaveButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dialogs, setDialogs] = useState<Dialogs | null>(null);
    const [open, setOpen] = useState<Open>('none');
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(Boolean(initiallySaved));
    const [message, setMessage] = useState('');
    const openRef = useRef<Open>('none');
    const show = (next: Open) => {
        openRef.current = next;
        setOpen(next);
    };
    const item = legacyMealId
        ? { legacyMealId }
        : { legacyRestaurantId: legacyRestaurantId ?? undefined };
    const hasItem = Boolean(legacyMealId || legacyRestaurantId);

    useEffect(() => {
        if (initiallySaved !== undefined) setSaved(initiallySaved);
    }, [initiallySaved]);

    useEffect(() => {
        if (initiallySaved !== undefined || !hasItem || !hasSignedInHint()) {
            return;
        }
        let cancelled = false;
        v2Session<{ itemSavedListIds: string[] }>(
            ITEM_SAVED_LIST_IDS,
            itemVariables(item),
        )
            .then(
                data =>
                    !cancelled && setSaved(data.itemSavedListIds.length > 0),
            )
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [legacyMealId, legacyRestaurantId]);

    if (!hasItem) return null;

    const prefetch = () => {
        loadDialogs().catch(() => undefined);
    };

    const openDialog = async () => {
        setLoading(true);
        setMessage('');
        try {
            setDialogs(await loadDialogs());
            show(hasSignedInHint() ? 'save' : 'auth');
        } catch {
            // Chunk failed to load (offline); the button stays usable.
        } finally {
            setLoading(false);
        }
    };

    const onCloseAutoFocus = (event: Event) => {
        event.preventDefault();
        if (openRef.current === 'none') buttonRef.current?.focus();
    };

    return (
        <div
            className={cn(
                'tw',
                variant === 'icon'
                    ? 'contents'
                    : 'flex flex-col items-start gap-1',
            )}
        >
            {variant === 'icon' ? (
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={openDialog}
                    onPointerEnter={prefetch}
                    onFocus={prefetch}
                    aria-haspopup="dialog"
                    aria-busy={loading || undefined}
                    aria-pressed={saved}
                    title={saved ? 'Saved' : 'Save'}
                    className={cn(
                        'flex size-9 items-center justify-center rounded-full bg-surface/90 shadow-card outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                        saved ? 'text-primary' : 'text-ink hover:text-primary',
                        className,
                    )}
                >
                    <BookmarkIcon filled={saved} />
                    <span className="sr-only">
                        {saved ? `Saved: ${itemName}` : `Save ${itemName}`}
                    </span>
                </button>
            ) : (
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={openDialog}
                    onPointerEnter={prefetch}
                    onFocus={prefetch}
                    aria-haspopup="dialog"
                    aria-busy={loading || undefined}
                    className={cn(
                        'inline-flex min-h-10 items-center gap-2 rounded-md border px-4 text-subtitle-2 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                        saved
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border-strong text-ink hover:border-ink hover:bg-surface-subtle',
                        className,
                    )}
                >
                    <BookmarkIcon filled={saved} />
                    {saved ? 'Saved' : 'Save'}
                </button>
            )}
            <p
                role="status"
                className={cn(
                    'text-caption text-ink-muted empty:hidden',
                    variant === 'icon' && 'sr-only',
                )}
            >
                {message}
            </p>
            {dialogs && (
                <>
                    <dialogs.AuthDialog
                        open={open === 'auth'}
                        onOpenChange={next => !next && show('none')}
                        onSignedIn={() => show('save')}
                        title="Sign in to save to a list"
                        onCloseAutoFocus={onCloseAutoFocus}
                    />
                    <dialogs.SaveToListDialog
                        open={open === 'save'}
                        onOpenChange={next => !next && show('none')}
                        onNeedsSignIn={() => show('auth')}
                        onSaved={(isSaved, text) => {
                            setSaved(isSaved);
                            setMessage(text);
                        }}
                        onCloseAutoFocus={onCloseAutoFocus}
                        item={item}
                        itemName={itemName}
                    />
                </>
            )}
        </div>
    );
};
